import type { TtsSettings } from "../../shared/tts.js";
import { DEFAULT_TTS_SETTINGS } from "../../shared/tts.js";

export type PlaybackStatus =
  | "idle"
  | "loading"
  | "playing"
  | "paused"
  | "previewing"
  | "stopping"
  | "error";

export type OutputOwner = "none" | "main" | "preview";

export interface PlaybackControllerState {
  status: PlaybackStatus;
  activeOutput: OutputOwner;
  sessionId: number;
  commandId: number;
  progress: number;
  mediaDuration: number;
  timingDuration: number;
  currentTime: number;
  volume: number;
  ttsSettings: TtsSettings;
  error: string | null;
  activity: string | null;
  previewingVoice: string | null;
  detailsVersion: number;
}

export type PlaybackControllerAction =
  | {
      type: "command:start";
      status: PlaybackStatus;
      activeOutput: OutputOwner;
      sessionId: number;
      commandId: number;
      activity?: string | null;
      clearError?: boolean;
      previewingVoice?: string | null;
      resetTimeline?: boolean;
    }
  | {
      type: "transport:reset";
      error?: string | null;
      activity?: string | null;
    }
  | {
      type: "transport:update";
      progress?: number;
      mediaDuration?: number;
      timingDuration?: number;
      currentTime?: number;
    }
  | { type: "status:set"; status: PlaybackStatus; activeOutput?: OutputOwner }
  | { type: "activity:set"; activity: string | null }
  | { type: "error:set"; error: string | null; status?: PlaybackStatus }
  | { type: "error:clear" }
  | { type: "preview:set"; previewingVoice: string | null }
  | { type: "settings:hydrate"; settings: TtsSettings }
  | { type: "settings:set"; settings: TtsSettings }
  | { type: "volume:set"; volume: number }
  | { type: "details:bump" };

export function createInitialPlaybackControllerState(): PlaybackControllerState {
  return {
    status: "idle",
    activeOutput: "none",
    sessionId: 0,
    commandId: 0,
    progress: 0,
    mediaDuration: 0,
    timingDuration: 0,
    currentTime: 0,
    volume: 1,
    ttsSettings: DEFAULT_TTS_SETTINGS,
    error: null,
    activity: null,
    previewingVoice: null,
    detailsVersion: 0,
  };
}

export function playbackControllerReducer(
  state: PlaybackControllerState,
  action: PlaybackControllerAction
): PlaybackControllerState {
  switch (action.type) {
    case "command:start":
      return {
        ...state,
        status: action.status,
        activeOutput: action.activeOutput,
        sessionId: action.sessionId,
        commandId: action.commandId,
        activity: action.activity ?? state.activity,
        error: action.clearError ? null : state.error,
        previewingVoice:
          action.previewingVoice === undefined
            ? action.activeOutput === "preview"
              ? state.previewingVoice
              : null
            : action.previewingVoice,
        progress: action.resetTimeline ? 0 : state.progress,
        mediaDuration: action.resetTimeline ? 0 : state.mediaDuration,
        timingDuration: action.resetTimeline ? 0 : state.timingDuration,
        currentTime: action.resetTimeline ? 0 : state.currentTime,
      };

    case "transport:reset":
      return {
        ...state,
        status: action.error ? "error" : "idle",
        activeOutput: "none",
        progress: 0,
        mediaDuration: 0,
        timingDuration: 0,
        currentTime: 0,
        activity: action.activity ?? null,
        error: action.error ?? null,
        previewingVoice: null,
      };

    case "transport:update":
      return {
        ...state,
        progress: action.progress ?? state.progress,
        mediaDuration: action.mediaDuration ?? state.mediaDuration,
        timingDuration: action.timingDuration ?? state.timingDuration,
        currentTime: action.currentTime ?? state.currentTime,
      };

    case "status:set":
      return {
        ...state,
        status: action.status,
        activeOutput: action.activeOutput ?? state.activeOutput,
      };

    case "activity:set":
      return {
        ...state,
        activity: action.activity,
      };

    case "error:set":
      return {
        ...state,
        error: action.error,
        status: action.status ?? state.status,
      };

    case "error:clear":
      return {
        ...state,
        error: null,
      };

    case "preview:set":
      return {
        ...state,
        previewingVoice: action.previewingVoice,
        status:
          action.previewingVoice === null && state.activeOutput === "preview"
            ? "idle"
            : action.previewingVoice === null
              ? state.status
              : "previewing",
        activeOutput:
          action.previewingVoice === null && state.activeOutput === "preview"
            ? "none"
            : action.previewingVoice === null
              ? state.activeOutput
              : "preview",
      };

    case "settings:hydrate":
    case "settings:set":
      return {
        ...state,
        ttsSettings: action.settings,
      };

    case "volume:set":
      return {
        ...state,
        volume: action.volume,
      };

    case "details:bump":
      return {
        ...state,
        detailsVersion: (state.detailsVersion + 1) % 1000000,
      };
  }
}
